'use client'

import { FC } from 'react'
import { useGetTranslations, useAppConfig } from '@/root/shared-hooks'
import { BottomSliderContainer } from '@/root/components/tuuleliinid/BottomSliderContainer/BottomSliderContainer'
import { Button } from '@/root/ui/tuuleliinid'
import { BackIcon, BottomArrowIcon } from '@/root/ui/shared'
import { testIds } from '@/root/shared-consts'

interface EditModeProps {
  mode?: 'edit'
  canFinish: boolean
  isLoading: boolean
  isFinishing: boolean
  hasExtraPayment?: boolean
  onCancel: () => void
  onFinish: () => Promise<void>
}

interface PaymentModeProps {
  mode: 'payment'
  canPay: boolean
  isProcessing: boolean
  isCancelling: boolean
  toPayAmount: number
  onCancel: () => void
  onPay: () => void
}

type EditActionsProps = EditModeProps | PaymentModeProps

export const EditActions: FC<EditActionsProps> = (props) => {
  const { getTranslation } = useGetTranslations()
  const { formatMoney } = useAppConfig()

  const isPaymentMode = props.mode === 'payment'

  if (isPaymentMode) {
    const { canPay, isProcessing, isCancelling, toPayAmount, onCancel, onPay } = props
    const isBusy = isProcessing || isCancelling

    return (
      <BottomSliderContainer>
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col-reverse gap-4 md:flex-row md:justify-between">
            {/* Cancel Button */}
            <Button
              data-testid={testIds.edit.paymentCancel}
              variant="outlined"
              onClick={onCancel}
              disabled={isBusy}
              className="group h-20 w-full text-neutral-medium-light hover:border-transparent hover:bg-base-medium-light hover:text-white disabled:bg-transparent disabled:border-neutral-medium-lighter disabled:text-neutral-medium-lighter disabled:hover:bg-transparent disabled:hover:border-neutral-medium-lighter disabled:hover:text-neutral-medium-lighter transition-colors md:w-64"
            >
              <div className="size-6">
                <span className="group-hover:hidden group-disabled:block"><BackIcon color="neutral-medium-light" /></span>
                <span className="hidden group-hover:block group-disabled:hidden"><BackIcon color="white" /></span>
              </div>
              <div className="flex size-max flex-col flex-wrap items-end justify-around">
                <div className="text-end text-2xl leading-tight font-bold">{getTranslation('editPaymentCancelTitle')}</div>
                <div className="text-end text-sm leading-tight">{getTranslation('editPaymentCancelSubtitle')}</div>
              </div>
            </Button>

            {/* Pay Button */}
            <Button
              data-testid={testIds.edit.pay}
              variant="gray"
              onClick={onPay}
              disabled={!canPay || isBusy}
              className="flex min-h-20 w-full flex-1 items-center justify-between px-4 md:w-64 md:flex-none"
            >
              <div className="text-start">
                <div className="text-2xl leading-[1.2]">
                  {getTranslation('editReservationPaymentButton', { amount: formatMoney(toPayAmount) })}
                </div>
                <div className="text-sm leading-[1.2]">
                  {isProcessing
                    ? getTranslation('editButtonFinishAndPayProcessing')
                    : getTranslation('editReservationPaymentButtonSubtitle')}
                </div>
              </div>
              <div className="mt-2.5 size-6">
                <BottomArrowIcon color="white" width={16} ignoreRtl className="ltr:-rotate-90" />
              </div>
            </Button>
          </div>
        </div>
      </BottomSliderContainer>
    )
  }

  const { canFinish, isLoading, isFinishing, hasExtraPayment, onCancel, onFinish } = props

  const finishTitle = hasExtraPayment
    ? getTranslation('editButtonFinishAndPayTitle')
    : getTranslation('editButtonFinishTitle')

  const finishSubtitle = isFinishing
    ? (hasExtraPayment
        ? getTranslation('editButtonFinishAndPayProcessing')
        : getTranslation('editButtonFinishing'))
    : hasExtraPayment
      ? getTranslation('editButtonFinishAndPaySubtitle')
      : getTranslation('editButtonFinishSubtitle')

  return (
    <BottomSliderContainer>
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col-reverse gap-4 md:flex-row md:justify-between">
          {/* Cancel Button */}
          <Button
            data-testid={testIds.edit.cancel}
            variant="outlined"
            onClick={onCancel}
            disabled={isLoading || isFinishing}
            className="group h-20 w-full text-neutral-medium-light hover:border-transparent hover:bg-base-medium-light hover:text-white disabled:bg-transparent disabled:border-neutral-medium-lighter disabled:text-neutral-medium-lighter disabled:hover:bg-transparent disabled:hover:border-neutral-medium-lighter disabled:hover:text-neutral-medium-lighter transition-colors md:w-64"
          >
            <div className="size-6">
              <span className="group-hover:hidden group-disabled:block"><BackIcon color="neutral-medium-light" /></span>
              <span className="hidden group-hover:block group-disabled:hidden"><BackIcon color="white" /></span>
            </div>
            <div className="flex size-max flex-col flex-wrap items-end justify-around">
              <div className="text-end text-2xl leading-tight font-bold">{getTranslation('editButtonCancelTitle')}</div>
              <div className="text-end text-sm leading-tight">{getTranslation('editButtonCancelSubtitle')}</div>
            </div>
          </Button>

          {/* Finish Edit Button */}
          <Button
            data-testid={testIds.edit.finish}
            variant="gray"
            onClick={onFinish}
            disabled={!canFinish || isLoading || isFinishing}
            className="flex h-20 flex-1 items-center justify-between px-4 md:w-64 md:flex-none"
          >
            <div className="text-start">
              <div className="text-2xl leading-[1.2]">{finishTitle}</div>
              <div className="text-sm leading-[1.2]">{finishSubtitle}</div>
            </div>
            <div className="mt-2.5 size-6">
              <BottomArrowIcon color="white" width={16} ignoreRtl className="ltr:-rotate-90" />
            </div>
          </Button>
        </div>
      </div>
    </BottomSliderContainer>
  )
}
